import { useEffect, useState } from 'react'
import type { Employee } from '@lms/shared-types'
import { ConfirmationDialog } from '../../primitives/ConfirmationDialog'
import { EmployeePickerInput } from '../../primitives/EmployeePickerInput'

/** A minimal {id, name} pair — all the dialog needs to pin one side. */
export interface NamedPerson {
  id: string
  name: string
}

interface AssignApproverDialogProps {
  open: boolean
  onClose: () => void
  /** Called with (memberId, approverId) when the assignment is confirmed. */
  onConfirm: (memberId: string, approverId: string) => void
  loading?: boolean
  /** Fix the approver side (opened from an approver card / "Add member"). When
   *  omitted, the dialog shows an approver picker ("Make approver"). */
  presetApprover?: NamedPerson | undefined
  /** Fix the member side (opened from an unassigned row). When omitted, the
   *  dialog shows a member picker. */
  presetMember?: NamedPerson | undefined
  /** Name of a person's CURRENT approver (or null) — drives the move warning
   *  so the admin sees they're pulling someone off their existing approver. */
  currentApproverName: (employeeId: string) => string | null
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-2)',
  marginBottom: 6,
}

const fixedValueStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
}

/**
 * One dialog for every approver assignment on the Approvers tab:
 *  - Add member (approver preset, pick the member)
 *  - Make approver (pick both sides)
 *  - Assign an unassigned person (member preset, pick the approver)
 *
 * Whichever side is preset renders as a fixed label; the other side is an
 * `EmployeePickerInput`. When the chosen member already reports to someone
 * else, a move warning surfaces before confirm — assignments are one-per-
 * employee, so assigning here removes them from their current approver.
 */
export function AssignApproverDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
  presetApprover,
  presetMember,
  currentApproverName,
}: AssignApproverDialogProps): JSX.Element | null {
  const [approver, setApprover] = useState<Employee | null>(null)
  const [member, setMember] = useState<Employee | null>(null)

  // Reset the pickers each time the dialog (re)opens so a previous selection
  // never bleeds into the next assignment.
  useEffect(() => {
    if (open) {
      setApprover(null)
      setMember(null)
    }
  }, [open, presetApprover?.id, presetMember?.id])

  if (!open) return null

  const approverId = presetApprover?.id ?? approver?.id ?? null
  const approverName = presetApprover?.name ?? approver?.name ?? null
  const memberId = presetMember?.id ?? member?.id ?? null
  const memberName = presetMember?.name ?? member?.name ?? null

  const bothChosen = approverId != null && memberId != null
  const selfApproval = bothChosen && approverId === memberId

  // Move warning: the member already has a (different) approver.
  const existingApprover = memberId ? currentApproverName(memberId) : null
  const isMove =
    existingApprover != null &&
    approverName != null &&
    existingApprover !== approverName

  const confirmLabel = isMove ? 'Move & assign' : 'Assign'

  return (
    <ConfirmationDialog
      open={open}
      onClose={onClose}
      onConfirm={() => {
        if (bothChosen && !selfApproval) onConfirm(memberId, approverId)
      }}
      title={presetMember ? 'Assign an approver' : 'Add a member'}
      subtitle={
        presetApprover
          ? `Choose who should report to ${presetApprover.name}.`
          : presetMember
            ? `Choose ${presetMember.name}'s approver.`
            : 'Choose an approver and the first person to report to them.'
      }
      confirmLabel={confirmLabel}
      confirmDisabled={!bothChosen || selfApproval}
      loading={loading}
      variant={isMove ? 'danger' : 'default'}
      data-testid="assign-approver-dialog"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Approver side */}
        <div>
          <span style={fieldLabelStyle}>Approver</span>
          {presetApprover ? (
            <div style={fixedValueStyle}>{presetApprover.name}</div>
          ) : (
            <EmployeePickerInput
              value={approver}
              onChange={setApprover}
              testId="assign-approver-picker"
              placeholder="Search for an approver"
              excludeId={memberId ?? undefined}
              portalMenu
            />
          )}
        </div>

        {/* Member side */}
        <div>
          <span style={fieldLabelStyle}>Member</span>
          {presetMember ? (
            <div style={fixedValueStyle}>{presetMember.name}</div>
          ) : (
            <EmployeePickerInput
              value={member}
              onChange={setMember}
              testId="assign-member-picker"
              placeholder="Search for a member"
              excludeId={approverId ?? undefined}
              portalMenu
            />
          )}
        </div>

        {selfApproval ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--danger-fg)' }}>
            Someone cannot be their own approver.
          </p>
        ) : isMove ? (
          <p
            data-testid="assign-approver-move-warning"
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--danger-fg)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              padding: '8px 10px',
              lineHeight: 1.5,
            }}
          >
            {memberName} currently reports to <strong>{existingApprover}</strong>.
            Assigning them here will move them off {existingApprover} to{' '}
            <strong>{approverName}</strong>.
          </p>
        ) : null}
      </div>
    </ConfirmationDialog>
  )
}
